import { userEntryToNumber, numberToUserDisplay } from '@ebiz/pretty-num';

const sweepType = {
  linear: 'LIN',
  decade: 'DEC',
  octave: 'OCT',
};
const orderedPulseParams = [
  'lowValue',
  'highValue',
  'delayTime',
  'riseTime',
  'fallTime',
  'pulseWidth',
  'period',
  'cycles',
];
const orderedSineParams = ['offset', 'peakAmplitude', 'frequency', 'delayTime', 'dampingFactor', 'phase', 'cycles'];
const orderedSweepParams = ['sweepType', 'points', 'startFrequency', 'stopFrequency'];
const orderedTransientParams = ['step', 'stopTime', 'startTime', 'maxDelayTime'];
const orderedTransientParams2 = ['stopTime'];
const directives = ['!.AC', '!.NOISE', '!.TRAN', '!.DC', '!.OP'];
const transientRegExp = /^[.+-\d].*/;
const symbolPattern = /(\S+\s+)(\S+)(\s+)([+-\d]+)(\s+)([+-\d]+)(\s+)(\S+)(.*)/;

export class SpiceSchematic {
  constructor(partsData, schematicTemplate, digits, templateParts) {
    this._partsData = partsData;
    this._schematic = schematicTemplate.replace(/\r?\n/g, '\r\n');
    this._digits = digits ? digits : 3;
    this._templateParts = templateParts ?? partsData;
  }

  get schematic() {
    return this._schematic;
  }

  /* Exported Functions */
  getSymbolValue(name) {
    const symbolRange = this._findSymbolByInstanceName(name);
    if (!symbolRange) {
      throw new Error(`Couldn't find ${name} in schematic schematic`);
    }
    const attributeLine = this._findSymbolAttributeByName(symbolRange, 'Value');
    if (!attributeLine) {
      throw new Error(`Couldn't find ${name} Value in schematic`);
    }
    const attributeValue = attributeLine[0].split('Value ')[1];
    return attributeValue;
  }

  getSymbolAttribute(name, attribute) {
    const symbolRange = this._findSymbolByInstanceName(name);
    if (!symbolRange) {
      throw new Error(`Couldn't find ${name} in schematic schematic`);
    }
    const attributeLine = this._findSymbolAttributeByName(symbolRange, attribute);
    if (!attributeLine) {
      throw new Error(`Couldn't find ${name} ${attribute} in schematic schematic`);
    }
    const attributeValue = attributeLine[0].split(attribute + ' ')[1];
    return attributeValue;
  }

  removeFlagElements(name) {
    if (typeof name == 'undefined' || name == null || name == '') {
      throw new Error(`Target Flag name is required.`);
    }
    const lines = this._makeLines();
    let result = '';
    for (let i = lines.length - 1; i > -1; i--) {
      let line = lines[i];
      if (line[0].toLowerCase().startsWith('flag')) {
        if (!line[0].endsWith(name)) {
          result = line[0] + '\n' + result;
        }
      } else {
        result = line[0] + '\n' + result;
      }
    }
    this._schematic = result;
    return this;
  }

  selectAnalysisType(analysisType) {
    const lines = this._makeLines();
    let result = '';
    for (let i = lines.length - 1; i > -1; i--) {
      let line = lines[i];
      if (line[0].toLowerCase().startsWith('text') && this._isDirectiveLine(line[0])) {
        if (line[0].toLowerCase().indexOf(`!.${analysisType.toLowerCase()}`) > -1) {
          result = line[0] + '\r\n' + result;
        }
      } else {
        result = line[0] + '\r\n' + result;
      }
    }
    this._schematic = result;
    return this;
  }

  updateFlagName(name, replacementName) {
    if (typeof name == 'undefined' || name == null || name == '') {
      throw new Error(`Target flag name is required.`);
    }
    if (typeof replacementName == 'undefined' || replacementName == null || replacementName == '') {
      throw new Error(`Replacement name for targeted flags is required.`);
    }
    const lines = this._makeLines();
    let result = '';
    for (let i = lines.length - 1; i > -1; i--) {
      let line = lines[i];
      if (line[0].toLowerCase().startsWith('flag')) {
        result = (line[0].endsWith(name) ? line[0].replace(name, replacementName) : line[0]) + '\n' + result;
      } else {
        result = line[0] + '\n' + result;
      }
    }
    this._schematic = result;
    return this;
  }

  updateSymbol(name, genericPartNumber, deleteIfNotFound, rails) {
    if (rails) {
      this._untiePinsFromRails(name, rails);
      const deleteNotFound = this._replaceSymbol(name, genericPartNumber, rails, deleteIfNotFound);
      if (!deleteNotFound) {
        this._tiePinsToRails(name, genericPartNumber, rails);
      }
    } else {
      this._replaceSymbol(name, genericPartNumber, rails, deleteIfNotFound);
    }

    return this;
  }

  updateSymbolValue(name, value) {
    const symbolRange = this._findSymbolByInstanceName(name);
    if (!symbolRange) {
      throw new Error(`No symbol found with reference designator ${name}`);
    }
    const attributeLine = this._findSymbolAttributeByName(symbolRange, 'Value');
    if (!attributeLine) {
      throw new Error(`Attribute Value not found for reference designator ${name}`);
    }
    const attributePattern = /(\S+\s+\S+\s+)(.*)/;
    const pieces = attributePattern.exec(attributeLine[0]);
    if (!pieces) {
      throw new Error(`Attribute Value for ${name} doesn't have expected structure`);
    }
    let newValue = numberToUserDisplay(value, 'Spice', this._digits, { showZeroes: false });
    const newSymattr = attributeLine[0].replace(attributePattern, `$1${newValue}`);
    let result =
      this._schematic.substring(0, attributeLine[1]) +
      newSymattr +
      this._schematic.substring(attributeLine[1] + attributeLine[0].length);
    this._schematic = result;
    return this;
  }

  updateSymbolAttribute(name, attribute, value) {
    this._schematic = this._updateSymbolAttribute(name, attribute, value);
    return this;
  }

  updateSubcircuitSymbol(name, symbolName) {
    const symbolRange = this._findSymbolByInstanceName(name);
    const symbolPieces = symbolPattern.exec(symbolRange[0][0]);
    if (!symbolPieces) {
      throw new Error(`The SYMBOL row for the specified reference designator doesn't have the expected format.`);
    }
    let part = {
      name: symbolPieces[2],
      x: parseInt(symbolPieces[4]),
      y: parseInt(symbolPieces[6]),
      transform: symbolPieces[8],
    };

    const newSymbol = symbolRange[0][0].replace(
      symbolPattern,
      `$1${symbolName}$3${part.x}$5${part.y}$7${part.transform}$9`
    );
    let result =
      this._schematic.substring(0, symbolRange[0][1]) +
      newSymbol +
      this._schematic.substring(symbolRange[0][1] + symbolRange[0][0].length);
    this._schematic = result;
    return this;
  }

  updateTextContent(placeholderText, replacementText) {
    const lines = this._makeLines();
    let textLines = lines.filter((line) => line[0].toLowerCase().startsWith('text') && line[0].indexOf(' !') == -1);
    let result = '';
    textLines.forEach((line) => {
      let segments = line[0].match(/[\w'\w;]+(?:[^\w\n]+[\w'\w;]+){0,4}/g);
      if (placeholderText == '') {
        result = this._schematic.replace(line[0], `${segments[0]} ${replacementText}`);
        this._schematic = result;
      } else {
        if (segments[1].includes(placeholderText)) {
          let newTextContent = segments[1].replace(placeholderText, replacementText);
          result = this._schematic.replace(line[0], `${segments[0]} ${newTextContent}`);
          this._schematic = result;
        }
      }
    });
    return this;
  }

  setSymbolAttribute(name, attribute, value) {
    const symbolRange = this._findSymbolByInstanceName(name);
    if (!symbolRange) {
      throw new Error(`Couldn't find ${name} in schematic schematic`);
    }
    const attributeLine = this._findSymbolAttributeByName(symbolRange, attribute);
    if (attributeLine) {
      if (value === null) {
        // attributeLine is [the line text, the index where it starts]
        const hasCarriageReturn = this._schematic.substr(attributeLine[1] + attributeLine[0].length, 1) === '\r';
        this._schematic =
          this._schematic.substring(0, attributeLine[1]) +
          this._schematic.substring(attributeLine[1] + attributeLine[0].length + (hasCarriageReturn ? 2 : 1));
      } else {
        this._schematic = this._updateSymbolAttribute(name, attribute, value);
      }
    } else if (value !== null) {
      this._schematic = this._addSymbolAttribute(name, attribute, value);
    }
    return this;
  }

  /* Exported Voltage Source Functions */
  updateDCVoltageSource(name, voltage) {
    let result = this._updateDCSource(name, voltage);
    this._schematic = result;
    return this;
  }

  updateACVoltageSource(name, voltage, phase) {
    let result = this._updateACSource(name, voltage, phase);
    this._schematic = result;
    return this;
  }

  updateSineVoltageSource(name, parameters) {
    let result = this._updateSineSource(name, parameters);
    this._schematic = result;
    return this;
  }

  updatePulseVoltageSource(name, parameters) {
    let result = this._updatePulseSource(name, parameters);
    this._schematic = result;
    return this;
  }

  /* Exported Current Source Functions */
  updateDCCurrentSource(name, current) {
    let result = this._updateDCSource(name, current);
    this._schematic = result;
    return this;
  }

  updateACCurrentSource(name, current, phase) {
    let result = this._updateACSource(name, current, phase);
    this._schematic = result;
    return this;
  }

  updateSineCurrentSource(name, parameters) {
    let result = this._updateSineSource(name, parameters);
    this._schematic = result;
    return this;
  }

  updatePulseCurrentSource(name, parameters) {
    let result = this._updatePulseSource(name, parameters);
    this._schematic = result;
    return this;
  }

  /* Exported Simulation Directive Functions */
  updateTransientAnalysis(parameters) {
    let line = this._findSimulationTypeCommand('tran');
    if (!line) {
      throw new Error(`No transient simulation directive found in schematic`);
    }
    let segments = line[0].split(' !.');
    let pieces = segments[segments.length - 1].split(' ');
    let values = pieces.slice(1);
    let orderedParams = this._useSecondTransientSyntax(values) ? orderedTransientParams2 : orderedTransientParams;
    if (this._checkParameters(parameters, orderedParams)) {
      if (values.length - 1 >= this._getMaxIndex(orderedParams, parameters)) {
        let newValues = this._getUpdatedValues(values, parameters, orderedParams);
        let value = `TRAN ${newValues.join(' ')}`;
        let result = this._updateSimulationType(value);
        this._schematic = result;
        return this;
      }
      throw new Error(`One of the parameters provided is not contained in the sequence of values in the template`);
    }
    throw new Error(`One of the parameters provided is not valid for the directive used in the template`);
  }

  updateACAnalysis(parameters) {
    let line = this._findSimulationTypeCommand('ac');
    if (!line) {
      throw new Error(`No ac simulation directive found in schematic`);
    }
    let segments = line[0].split(' !.');
    let pieces = segments[segments.length - 1].split(' ');
    let values = pieces.slice(1);
    let sweepTypes = ['DEC', 'LIN', 'OCT'];
    if (values.length - 1 >= this._getMaxIndex(orderedSweepParams, parameters)) {
      let newValues = this._getUpdatedValues(values, parameters, orderedSweepParams, sweepTypes);
      let value = `AC ${newValues.join(' ')}`;
      let result = this._updateSimulationType(value);
      this._schematic = result;
      return this;
    }
    throw new Error(`One of the parameters provided is not contained in the sequence of values in the template`);
  }

  updateNoiseAnalysis(parameters) {
    let line = this._findSimulationTypeCommand('noise');
    if (!line) {
      throw new Error(`No noise simulation directive found in schematic`);
    }
    let segments = line[0].split(' !.');
    let pieces = segments[segments.length - 1].split(' ');
    let values = pieces.slice(3);
    let sweepTypes = ['DEC', 'LIN', 'OCT'];
    if (values.length - 1 >= this._getMaxIndex(orderedSweepParams, parameters)) {
      let newValues = this._getUpdatedValues(values, parameters, orderedSweepParams, sweepTypes);
      let value = `NOISE ${pieces[1]} ${pieces[2]} ${newValues.join(' ')}`;
      let result = this._updateSimulationType(value);
      this._schematic = result;
      return this;
    }
    throw new Error(`One of the parameters provided is not contained in the sequence of values in the template`);
  }

  getComponentValue(name) {
    const valueAsString = this.getSymbolAttribute(name, 'Value');
    return userEntryToNumber(valueAsString);
  }

  setComponentValue(name, value) {
    const displayValue = numberToUserDisplay(value, 'Spice', this._digits, { showZeroes: false });
    let result = this._updateSymbolAttribute(name, 'Value', displayValue);
    this._schematic = result;
    return this;
  }

  /* Internal Functions */
  _getOffset(newPartValue, oldPartValue) {
    return newPartValue - oldPartValue;
  }

  _findSymbolByInstanceName(name) {
    const lines = this._makeLines();
    let startBlock = -1;
    let endBlock = -1;
    let match = false;
    for (let index = 0; index < lines.length; index++) {
      const line = lines[index];
      const pieces = line[0].split(/\s/);
      if (pieces.length < 1) {
        continue;
      }
      switch (pieces[0].toLowerCase()) {
        case 'symbol':
          if (match) {
            endBlock = index;
          } else {
            startBlock = index;
          }
          break;
        case 'symattr':
          if (
            pieces.length > 2 &&
            pieces[1].toLowerCase() === 'instname' &&
            pieces[2].toLowerCase() === name.toLowerCase()
          ) {
            match = true;
          }
          break;
        case 'window':
        case 'pinattr':
          break;
        default:
          if (match) {
            endBlock = index;
          }
      }
      if (match && startBlock >= 0 && endBlock >= 0) {
        return lines.slice(startBlock, endBlock);
      }
    }
    if (match && startBlock >= 0) {
      return lines.slice(startBlock, lines.length);
    }
    return;
  }

  _findSymbolAttributeByName(schematicLines, name) {
    const line = schematicLines.find((line) => {
      const pieces = line[0].split(/\s/);
      if (pieces.length < 1) {
        return false;
      }
      return pieces[0].toLowerCase() === 'symattr' && pieces[1].toLowerCase() === name.toLowerCase();
    });
    return line;
  }

  _findSymbolAttributeInsertionPoint(schematicLines, terminator) {
    const lineStart = schematicLines[schematicLines.length - 1][1];
    const lineLength = schematicLines[schematicLines.length - 1][0].length;
    return lineStart + lineLength + terminator.length;
  }

  _findSimulationTypeCommand(type) {
    const SIMCMDS = ['!.ac', '!.dc', '!.noise', '!.op', '!.tf', '!.tran'];
    const lines = this._makeLines();
    const line = lines.find((value) => {
      const pieces = value[0].split(/\s/);
      return (
        pieces.length > 6 &&
        pieces[0].toLowerCase() === 'text' &&
        SIMCMDS.includes(pieces[5].toLowerCase()) &&
        pieces[5].toLowerCase() === '!.' + type.toLowerCase()
      );
    });
    return line;
  }

  _makeLines() {
    let index = 0;
    const lines = [];
    const linePattern = /([^\r\n]*)\r?\n/g;
    const schematicTerm =
      this._schematic[this._schematic.length - 1] === '\n' ? this._schematic : this._schematic + '\n';
    let line = null;
    while ((line = linePattern.exec(schematicTerm)) !== null) {
      lines.push([line[1], index]);
      index = linePattern.lastIndex;
    }
    return lines;
  }

  _findIndex(lines, value) {
    let result = lines.findIndex((line) => {
      let pieces = line[0].split(/\s/);
      return pieces[0].toLowerCase() == value.toLowerCase();
    });
    return result;
  }

  _addSymbolAttribute(name, attribute, value) {
    const symbolRange = this._findSymbolByInstanceName(name);
    if (!symbolRange) {
      throw new Error(`No symbol found with reference designator ${name}`);
    }
    const lineTerminator = this._schematic.substr(symbolRange[0][1] - 2, 2) === '\r\n' ? '\r\n' : '\n';
    const position = this._findSymbolAttributeInsertionPoint(symbolRange, lineTerminator);
    let result =
      this._schematic.substring(0, position) +
      `SYMATTR ${attribute} ${value}${lineTerminator}` +
      this._schematic.substring(position);

    return result;
  }

  _updateSymbolAttribute(name, attribute, value) {
    const symbolRange = this._findSymbolByInstanceName(name);
    if (!symbolRange) {
      throw new Error(`No symbol found with reference designator ${name}`);
    }
    const attributeLine = this._findSymbolAttributeByName(symbolRange, attribute);
    if (!attributeLine) {
      throw new Error(`Attribute ${attribute} not found for reference designator ${name}`);
    }
    const attributePattern = /(\S+\s+\S+\s+)(.*)/;
    const pieces = attributePattern.exec(attributeLine[0]);
    if (!pieces) {
      throw new Error(`Attribute ${attribute} for ${name} doesn't have expected structure`);
    }
    const newSymattr = attributeLine[0].replace(attributePattern, `$1${value}`);
    let result =
      this._schematic.substring(0, attributeLine[1]) +
      newSymattr +
      this._schematic.substring(attributeLine[1] + attributeLine[0].length);
    return result;
  }

  _updateSimulationType(type) {
    const SIMCMDS = ['ac', 'dc', 'noise', 'op', 'tf', 'tran'];
    const bits = type.toLowerCase().split(/\s+/);
    if (bits.length > 0 && SIMCMDS.includes(bits[0])) {
      const command = this._findSimulationTypeCommand(bits[0]);
      if (!command) {
        throw new Error(`Couldn't find a simulation type command in the schematic`);
      }
      const simulationPattern = /(\S+\s+\S+\s+\S+\s+\S+\s+\S+\s+)(.*)/;
      const pieces = simulationPattern.exec(command[0]);
      if (!pieces) {
        throw new Error(`Simulation type command doesn't have expected structure`);
      }
      const newCommand = command[0].replace(simulationPattern, `$1${'!.' + type}`);
      let result =
        this._schematic.substring(0, command[1]) +
        newCommand +
        this._schematic.substring(command[1] + command[0].length);
      return result;
    }
    throw new Error(`Invalid or missing command in '${type}'`);
  }

  _updateDCSource(name, value) {
    let newValue = numberToUserDisplay(value, 'Spice', this._digits, { showZeroes: false });
    return this._updateSymbolAttribute(name, 'Value', newValue);
  }

  _updateACSource(name, value, phase) {
    let newValue = numberToUserDisplay(value, 'Spice', this._digits, { showZeroes: false });
    let _value = `${newValue}`;
    if (typeof phase !== 'undefined') {
      let newPhase = numberToUserDisplay(phase, 'Spice', this._digits, { showZeroes: false });
      _value = `${newValue} ${newPhase}`;
    }
    return this._updateSymbolAttribute(name, 'Value', _value);
  }

  _updateSineSource(name, parameters) {
    let templateValue = this.getSymbolAttribute(name, 'Value');
    if (!templateValue.toLowerCase().startsWith('sine')) {
      throw new Error(`Template value not starting with SINE keyword`);
    }
    let values = templateValue.substring(templateValue.indexOf('(') + 1, templateValue.indexOf(')')).split(' ');
    if (values.length - 1 >= this._getMaxIndex(orderedSineParams, parameters)) {
      let newValues = this._getUpdatedValues(values, parameters, orderedSineParams);
      let value = `SINE(${newValues.join(' ')})`;
      return this._updateSymbolAttribute(name, 'Value', value);
    }
    throw new Error(`One of the parameters provided is not contained in the sequence of values in the template`);
  }

  _updatePulseSource(name, parameters) {
    let templateValue = this.getSymbolAttribute(name, 'Value');
    if (!templateValue.toLowerCase().startsWith('pulse')) {
      throw new Error(`Template value not starting with PULSE keyword`);
    }
    let values = templateValue.substring(templateValue.indexOf('(') + 1, templateValue.indexOf(')')).split(' ');
    if (values.length - 1 >= this._getMaxIndex(orderedPulseParams, parameters)) {
      let newValues = this._getUpdatedValues(values, parameters, orderedPulseParams);
      let value = `PULSE(${newValues.join(' ')})`;
      return this._updateSymbolAttribute(name, 'Value', value);
    }
    throw new Error(`One of the parameters provided is not contained in the sequence of values in the template`);
  }

  _validateSymbolPath(templateSymbolPath, targetSymbolPath) {
    let result = true;
    let templatePath = templateSymbolPath.substring(0, templateSymbolPath.lastIndexOf('\\'));
    let targetPath = targetSymbolPath.substring(0, targetSymbolPath.lastIndexOf('\\'));
    result = targetPath.toLowerCase() == templatePath.toLowerCase() ? true : false;
    return result;
  }

  _isDirectiveLine(line) {
    let result = false;
    for (let i = 0; i < directives.length; i++) {
      result = line.toLowerCase().indexOf(directives[i].toLowerCase()) > -1;
      if (result === true) return result;
    }
    return result;
  }

  _getMaxIndex(list, parameters) {
    let maxIndex = -1;
    for (let param in parameters) {
      maxIndex = Math.max(
        maxIndex,
        list.findIndex((item) => item == param)
      );
    }
    return maxIndex;
  }

  _getUpdatedValues(values, parameters, orderedList, sweepTypes) {
    let result = values.map((value, index) => {
      let isValid = parameters[orderedList[index]] !== null && typeof parameters[orderedList[index]] !== 'undefined';
      if (orderedList[index] == 'sweepType') {
        return isValid && sweepTypes.includes(sweepType[parameters[orderedList[index]]])
          ? sweepType[parameters[orderedList[index]]]
          : value.toUpperCase();
      } else {
        return isValid
          ? numberToUserDisplay(parameters[orderedList[index]], 'Spice', this._digits, { showZeroes: false })
          : value;
      }
    });
    return result;
  }

  _useSecondTransientSyntax(values) {
    let result = false;
    result = values.length == 1 || (values.length > 1 && !transientRegExp.test(values[1]));
    return result;
  }

  _checkParameters(parameters, orderedParams) {
    let result = true;
    for (let param in parameters) {
      result = orderedParams.indexOf(param) > -1 ? true : false;
      if (!result) return result;
    }
    return result;
  }

  _untiePinsFromRails(referenceDesignator, rails) {
    const part = this._getPartPieces(referenceDesignator);
    const templateSpiceNum = this._getTemplateSpiceNum(part);
    // If we couldn't find the part number in spice-parts, bail.
    if (!templateSpiceNum) {
      throw new Error(
        `Unable to find symbol path "${part.name}", referenced in template, in spiceParts collection. All parts in template to be updated must appear in the collection.`
      );
    }

    let result = this._schematic;
    const spicePart = templateSpiceNum[1];
    if (spicePart && spicePart.pins) {
      const applicablePins = this._getApplicablePins(spicePart, rails);
      for (const pin of applicablePins) {
        if (pin.x && pin.y) {
          const pinOffset = this._determineSymbolPinAdjustment(
            spicePart.offsetX,
            spicePart.offsetY,
            pin.x,
            pin.y,
            part.transform
          );
          const pinEnd = { x: pinOffset.x + part.x, y: pinOffset.y + part.y };
          const wireEnd = this._getStubWireEnd(part.transform, pin.orientation ?? '', pinEnd.x, pinEnd.y, 25);
          result = this._removeWireStubAt(result, pinEnd.x, pinEnd.y, wireEnd.x, wireEnd.y);
          result = this._removeFlagAt(result, wireEnd.x, wireEnd.y);
        }
      }
    }

    this._schematic = result;
  }

  _replaceSymbol(referenceDesignator, newPartNumber, rails, deleteIfNotFound) {
    const symbolRange = this._findSymbolByInstanceName(referenceDesignator);
    if (!symbolRange) {
      throw new Error('Reference designator ' + referenceDesignator + ' not found.');
    }
    let part = this._getPartPieces(referenceDesignator);
    const sourcePart = Object.assign({}, part);
    const templateSpiceNum = this._getTemplateSpiceNum(part);
    // If we couldn't find the part number in spice-parts, bail.
    if (!templateSpiceNum) {
      throw new Error(
        `Unable to find symbol path "${sourcePart.name}", referenced in template, in spiceParts collection. All parts in template to be updated must appear in the collection.`
      );
    }

    let result = '';
    const sourcePartDetails = templateSpiceNum[1];
    const targetPartDetails = this._partsData[newPartNumber];
    // Adjust (x,y) based on difference between parts (x,y)
    if (targetPartDetails) {
      part.name = targetPartDetails.symbolPath;
      if (rails) {
        const positionAdjustment = this._determineSymbolPinAdjustment(
          sourcePartDetails.offsetX,
          sourcePartDetails.offsetY,
          targetPartDetails.offsetX,
          targetPartDetails.offsetY,
          part.transform
        );
        part.x = sourcePart.x + positionAdjustment.x;
        part.y = sourcePart.y + positionAdjustment.y;
      } else {
        part.x = sourcePart.x + this._getOffset(targetPartDetails.offsetX, sourcePartDetails.offsetX);
        part.y = sourcePart.y + this._getOffset(targetPartDetails.offsetY, sourcePartDetails.offsetY);
      }

      const newSymbol = symbolRange[0][0].replace(
        symbolPattern,
        `$1${part.name}$3${part.x}$5${part.y}$7${part.transform}$9`
      );

      result =
        this._schematic.substring(0, symbolRange[0][1]) +
        newSymbol +
        this._schematic.substring(symbolRange[0][1] + symbolRange[0][0].length);

      deleteIfNotFound = false;
    } else {
      if (deleteIfNotFound === true) {
        // Since targetPartDetails is undefined, we use the x and y values of the template part to place the text.
        const newTextLine = `TEXT ${sourcePart.x} ${sourcePart.y} Left 2 ;Schematic generator \\ncould not find ${
          newPartNumber ? newPartNumber : `blank part`
        } \\nin LTspice parts library`;
        const newSymbol = symbolRange[0][0].replace(symbolPattern, newTextLine);
        result =
          this._schematic.substring(0, symbolRange[0][1]) +
          newSymbol +
          this._schematic.substring(
            symbolRange[symbolRange.length - 1][1] + symbolRange[symbolRange.length - 1][0].length
          );
      } else {
        throw new Error(
          `Unable to find ${newPartNumber ? newPartNumber : 'blank'} target partNumber in spiceParts collection`
        );
      }
    }

    this._schematic = result;
    return deleteIfNotFound;
  }

  _tiePinsToRails(referenceDesignator, newPartNumber, rails) {
    const part = this._getPartPieces(referenceDesignator);
    const templateSpiceNum = this._getTemplateSpiceNum(part);
    // If we couldn't find the part number in spice-parts, bail.
    if (!templateSpiceNum) {
      throw new Error(
        `Unable to find symbol path "${part.name}", referenced in template, in spiceParts collection. All parts in template to be updated must appear in the collection.`
      );
    }

    let result = this._schematic;
    const spicePart = this._partsData[newPartNumber];
    if (spicePart && spicePart.pins) {
      const applicablePins = this._getApplicablePins(spicePart, rails);
      for (const pin of applicablePins) {
        if (pin.x && pin.y && pin.connection) {
          const pinOffset = this._determineSymbolPinAdjustment(
            spicePart.offsetX,
            spicePart.offsetY,
            pin.x,
            pin.y,
            part.transform
          );
          const pinEnd = { x: pinOffset.x + part.x, y: pinOffset.y + part.y };
          const wireEnd = this._getStubWireEnd(part.transform, pin.orientation ?? '', pinEnd.x, pinEnd.y, 25);
          result = this._addWireStubAt(result, pinEnd.x, pinEnd.y, wireEnd.x, wireEnd.y);
          result = this._addFlagAt(result, wireEnd.x, wireEnd.y, rails[pin.connection]);
        }
      }
    }

    this._schematic = result;
  }

  _getApplicablePins(partDetails, rails) {
    const railKeys = Object.keys(rails);
    const applicablePins = partDetails.pins.filter((value) => {
      return value.connection && railKeys.includes(value.connection);
    });

    return applicablePins;
  }

  _getPartPieces(referenceDesignator) {
    const symbolRange = this._findSymbolByInstanceName(referenceDesignator);
    if (!symbolRange) {
      throw new Error('Reference designator ' + referenceDesignator + ' not found.');
    }
    const pieces = symbolPattern.exec(symbolRange[0][0]);
    if (!pieces) {
      throw new Error("The SYMBOL row for the specified reference designator doesn't have the expected format.");
    }

    const part = {
      name: pieces[2],
      x: parseInt(pieces[4]),
      y: parseInt(pieces[6]),
      transform: pieces[8],
    };

    return part;
  }

  _getTemplateSpiceNum(part) {
    let templateSpiceNum = Object.entries(this._templateParts).find((partData) => {
      return partData[1].symbolPath === part.name;
    });
    if (!templateSpiceNum) {
      if (this._templateParts === this._partsData) {
        throw new Error(
          `Unable to find symbol path "${part.name}", referenced in template, in spiceParts collection. All parts in template to be updated must appear in this collection unless a separate templateParts collection is supplied.`
        );
      } else {
        templateSpiceNum = Object.entries(this._partsData).find((partData) => {
          return partData[1].symbolPath === part.name;
        });
        if (!templateSpiceNum) {
          throw new Error(
            `Unable to find symbol path "${part.name}", referenced in template, in either templateParts collection or partsData collection.`
          );
        }
      }
    }
    return templateSpiceNum;
  }

  _determineSymbolPinAdjustment(partX, partY, pinX, pinY, orientation) {
    const xyRotationTable = {
      R0: { x: 1, y: 1 },
      R90: { x: -1, y: 1 },
      R180: { x: -1, y: -1 },
      R270: { x: 1, y: -1 },
      M0: { x: -1, y: 1 },
      M90: { x: 1, y: 1 },
      M180: { x: 1, y: -1 },
      M270: { x: -1, y: -1 },
    };
    //The orientation we read from the file has to be checked against our list at some point.
    //Here is as good as any.
    //see: https://dev.to/kingdaro/indexing-objects-in-typescript-1cgi
    function _hasKey(obj, key) {
      return key in obj;
    }
    // Adjust (x,y) based on difference between parts (x,y)
    const xDiff = pinX - partX;
    const yDiff = pinY - partY;
    let adjustment = { x: NaN, y: NaN };
    if (_hasKey(xyRotationTable, orientation)) {
      adjustment.x = xDiff * xyRotationTable[orientation].x;
      adjustment.y = yDiff * xyRotationTable[orientation].y;
    } else {
      throw new Error(
        'Orientation read from file is ' + orientation + '. This is not an orientation we know how to parse'
      );
    }
    return adjustment;
  }

  _getStubWireEnd(partDirection, pinDirection, xin, yin, length) {
    const xyRotationTable = {
      R0: { x: 1, y: 1 },
      R90: { x: -1, y: 1 },
      R180: { x: -1, y: -1 },
      R270: { x: 1, y: -1 },
      M0: { x: -1, y: 1 },
      M90: { x: 1, y: 1 },
      M180: { x: 1, y: -1 },
      M270: { x: -1, y: -1 },
    };
    const rotation = xyRotationTable[partDirection];
    const relativeEnd =
      pinDirection === 'top'
        ? { x: 0, y: -length }
        : pinDirection === 'bottom'
        ? { x: 0, y: length }
        : pinDirection === 'left'
        ? { x: -length, y: 0 }
        : pinDirection === 'right'
        ? { x: length, y: 0 }
        : { x: 0, y: 0 };

    return {
      x: xin + relativeEnd.x * rotation.x,
      y: yin + relativeEnd.y * rotation.y,
    };
  }

  _removeWireStubAt(schematic, x1, y1, x2, y2) {
    const regex = /wire\s+(-?\d+(.\d+)?)\s+(-?\d+(.\d+)?)\s+(-?\d+(.\d+)?)\s+(-?\d+(.\d+)?)/gi;
    let matched;
    while ((matched = regex.exec(schematic))) {
      const offset = schematic[regex.lastIndex] === '\r' ? 2 : 1;
      if (
        Math.round(parseFloat(matched[1])) === Math.round(x1) &&
        Math.round(parseFloat(matched[3])) === Math.round(y1) &&
        Math.round(parseFloat(matched[5])) === Math.round(x2) &&
        Math.round(parseFloat(matched[7])) === Math.round(y2)
      ) {
        return schematic.substring(0, matched.index) + schematic.substring(regex.lastIndex + offset);
      }
    }
    return schematic;
  }

  _addWireStubAt(schematic, x1, y1, x2, y2) {
    const pos = schematic.toLowerCase().indexOf('\nsymbol');
    if (pos > 0) {
      const separator = schematic[pos - 1] !== '\r' ? '\n' : '\r\n';
      return (
        schematic.substring(0, pos + 1) + `WIRE ${x1} ${y1} ${x2} ${y2}${separator}` + schematic.substring(pos + 1)
      );
    }
    return schematic;
  }

  _removeFlagAt(schematic, x, y) {
    const regex = /flag\s+(-?\d+(.\d+)?)\s+(-?\d+(.\d+)?)\s+[\w\d_-]+/gi;
    let matched;
    while ((matched = regex.exec(schematic))) {
      const offset = schematic[regex.lastIndex] === '\r' ? 2 : 1;
      if (
        Math.round(parseFloat(matched[1])) === Math.round(x) &&
        Math.round(parseFloat(matched[3])) === Math.round(y)
      ) {
        return schematic.substring(0, matched.index) + schematic.substring(regex.lastIndex + offset);
      }
    }
    return schematic;
  }

  _addFlagAt(schematic, x, y, flagName) {
    const pos = schematic.toLowerCase().indexOf('\nsymbol');
    if (pos > 0) {
      const separator = schematic[pos - 1] !== '\r' ? '\n' : '\r\n';
      return schematic.substring(0, pos + 1) + `FLAG ${x} ${y} ${flagName}${separator}` + schematic.substring(pos + 1);
    }
    return schematic;
  }
}
